<template>
  <form class="space-y-6" @submit.prevent="handleSubmit">
    <!-- Titre simplifié -->
    <div class="text-center">
      <h2 class="text-2xl font-semibold text-gray-900">
        Connexion
      </h2>
    </div>

    <!-- Champ Email -->
    <div class="space-y-1">
      <label for="email" class="block text-sm font-medium text-gray-700">
        Adresse email
        <span class="text-red-500 ml-1">*</span>
      </label>
      <div class="relative">
        <input
          id="email"
          v-model="formData.email"
          type="email"
          name="email"
          placeholder="votre.email@example.com"
          required
          :disabled="isSubmitting"
          autocomplete="email"
          :class="emailInputClasses"
          :aria-invalid="!!errors.email"
          @blur="validateField('email')"
          @input="clearFieldError('email')"
        />
        <div 
          v-show="!formData.email"
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-opacity duration-200"
        >
          <MailIcon class="h-4 w-4 text-gray-400" />
        </div>
      </div>
      <p v-if="errors.email" class="text-sm text-red-600" role="alert">
        {{ errors.email }}
      </p>
    </div>

    <!-- Champ Mot de passe -->
    <div class="space-y-1">
      <label for="password" class="block text-sm font-medium text-gray-700">
        Mot de passe
        <span class="text-red-500 ml-1">*</span>
      </label>
      <div class="relative">
        <input
          id="password"
          v-model="formData.password"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          placeholder="Votre mot de passe"
          required
          :disabled="isSubmitting"
          autocomplete="current-password"
          :class="passwordInputClasses"
          :aria-invalid="!!errors.password"
          @blur="validateField('password')"
          @input="clearFieldError('password')"
        />
        <div 
          v-show="!formData.password"
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-opacity duration-200"
        >
          <LockIcon class="h-4 w-4 text-gray-400" />
        </div>
        <button
          type="button"
          class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
          :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
          @click="togglePasswordVisibility"
        >
          <EyeIcon v-if="!showPassword" class="h-4 w-4" />
          <EyeOffIcon v-else class="h-4 w-4" />
        </button>
      </div>
      <p v-if="errors.password" class="text-sm text-red-600" role="alert">
        {{ errors.password }}
      </p>
    </div>

    <!-- Options -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <input
          id="remember-me"
          v-model="formData.rememberMe"
          type="checkbox"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label for="remember-me" class="text-sm text-gray-700 whitespace-nowrap">
          Se souvenir de moi
        </label>
      </div>

      <div class="text-sm">
        <button
          type="button"
          class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition-colors"
          @click="$emit('forgot-password')"
        >
          Mot de passe oublié ?
        </button>
      </div>
    </div>

    <!-- Bouton de soumission -->
    <button
      type="submit"
      :disabled="isSubmitting || !isFormValid"
      :class="submitButtonClasses"
    >
      <LoadingSpinner
        v-if="isSubmitting"
        size="sm"
        variant="white"
        :show-text="false"
      />
      <span v-else>Se connecter</span>
    </button>

    <!-- Lien inscription -->
    <div class="text-center">
      <p class="text-sm text-gray-600">
        Pas encore de compte ?
        <button
          type="button"
          class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition-colors"
          @click="$emit('switch-to-register')"
        >
          Créer un compte
        </button>
      </p>
    </div>

    <!-- Aide discrète -->
    <div class="text-center">
      <button
        type="button"
        class="text-xs text-gray-400 hover:text-gray-600 focus:outline-none focus:underline transition-colors"
        @click="showSecurityTips = !showSecurityTips"
      >
        <ShieldCheckIcon class="h-3 w-3 inline mr-1" />
        Conseils de sécurité
      </button>
    </div>

    <!-- Conseils de sécurité (collapsible) -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-32"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 max-h-32"
      leave-to-class="opacity-0 max-h-0"
    >
      <div v-if="showSecurityTips" class="bg-blue-50 border border-blue-200 rounded-lg p-3 overflow-hidden">
        <div class="text-xs text-blue-700 space-y-1">
          <p>• Utilisez un mot de passe unique et complexe</p>
          <p>• Ne partagez jamais vos identifiants</p>
          <p>• Déconnectez-vous sur un ordinateur partagé</p>
          <p>• Vérifiez toujours l'URL avant de saisir vos identifiants</p>
        </div>
      </div>
    </Transition>

    <!-- Notification pour les erreurs -->
    <Notification ref="notification" />
  </form>
</template>

<script setup lang="ts">
import Notification from '@/components/Notification.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import { authService } from '@/services/authService';
import type { AuthError, LoginRequest } from '@/types/auth';
import { Eye as EyeIcon, EyeOff as EyeOffIcon, Lock as LockIcon, Mail as MailIcon, ShieldCheck as ShieldCheckIcon } from 'lucide-vue-next';
import { computed, reactive, ref } from 'vue';

export interface LoginFormEmits {
  (e: 'submit', credentials: LoginRequest): void;
  (e: 'forgot-password'): void;
  (e: 'switch-to-register'): void;
}

const emit = defineEmits<LoginFormEmits>();

// Références
const notification = ref<InstanceType<typeof Notification> | null>(null);

// État du formulaire
const formData = reactive({
  email: '',
  password: '',
  rememberMe: false,
});

const errors = reactive({
  email: '',
  password: '',
});

const isSubmitting = ref(false);
const attemptCount = ref(0);
const lastAttemptTime = ref<Date | null>(null);
const showPassword = ref(false);
const showSecurityTips = ref(false);

// Gestion de la visibilité du mot de passe
const togglePasswordVisibility = (): void => {
  showPassword.value = !showPassword.value;
};

// Validation
const isFormValid = computed((): boolean => {
  return !!(
    formData.email && 
    formData.password && 
    !errors.email && 
    !errors.password
  );
});

// Classes CSS pour les champs
const emailInputClasses = computed((): string => {
  const paddingLeft = formData.email ? 'pl-3' : 'pl-10';
  const baseClasses = `block w-full ${paddingLeft} pr-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`;
  
  if (errors.email) {
    return `${baseClasses} border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500`;
  }
  
  return `${baseClasses} border-gray-300`;
});

const passwordInputClasses = computed((): string => {
  const paddingLeft = formData.password ? 'pl-3' : 'pl-10';
  const baseClasses = `block w-full ${paddingLeft} pr-10 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`;
  
  if (errors.password) {
    return `${baseClasses} border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500`;
  }
  
  return `${baseClasses} border-gray-300`;
});

// Classes CSS pour le bouton
const submitButtonClasses = computed((): string => {
  const baseClasses = 'w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';
  
  if (isSubmitting.value || !isFormValid.value) {
    return `${baseClasses} bg-gray-400 cursor-not-allowed`;
  }
  
  return `${baseClasses} bg-blue-600 hover:bg-blue-700`;
});

// Validation des champs
const validateField = (field: keyof typeof errors): void => {
  switch (field) {
    case 'email':
      if (!formData.email) {
        errors.email = 'L\'email est requis';
      } else if (!authService.isValidEmail(formData.email)) {
        errors.email = 'Format d\'email invalide';
      } else {
        errors.email = '';
      }
      break;
      
    case 'password':
      if (!formData.password) {
        errors.password = 'Le mot de passe est requis';
      } else if (formData.password.length < 3) {
        errors.password = 'Le mot de passe doit contenir au moins 3 caractères';
      } else {
        errors.password = '';
      }
      break;
  }
};

const validateForm = (): boolean => {
  validateField('email');
  validateField('password');
  
  return isFormValid.value;
};

const clearFieldError = (field: keyof typeof errors): void => {
  errors[field] = '';
};

const clearErrors = (): void => {
  errors.email = '';
  errors.password = '';
};

// Gestion des erreurs spécifiques
const getErrorMessage = (authError: AuthError): string => {
  switch (authError.code) {
    case 'INVALID_CREDENTIALS':
      return 'Email ou mot de passe incorrect. Vérifiez vos identifiants.';
    
    case 'EMAIL_NOT_VERIFIED':
      return 'Votre email n\'a pas été validé. Vérifiez votre boîte mail et cliquez sur le lien de validation.';
    
    case 'ACCOUNT_LOCKED':
      return 'Votre compte a été temporairement verrouillé suite à plusieurs tentatives de connexion échouées.';
    
    case 'NETWORK_ERROR':
      return 'Problème de connexion réseau. Vérifiez votre connexion internet.';
    
    case 'SERVER_ERROR':
      // Vérifier si c'est un message spécifique du serveur
      if (authError.message.includes('Email not verified')) {
        return 'Votre email n\'a pas été validé. Vérifiez votre boîte mail et cliquez sur le lien de validation.';
      }
      return 'Erreur serveur temporaire. Veuillez réessayer dans quelques instants.';
    
    case 'TOO_MANY_REQUESTS':
      return 'Trop de tentatives de connexion. Veuillez patienter avant de réessayer.';
    
    default:
      return authError.message || 'Une erreur inattendue s\'est produite lors de la connexion.';
  }
};

// Protection contre les attaques par force brute
const checkRateLimit = (): boolean => {
  const now = new Date();
  const maxAttempts = 5;
  const lockoutDuration = 15 * 60 * 1000; // 15 minutes

  // Réinitialiser le compteur si plus de 15 minutes se sont écoulées
  if (lastAttemptTime.value && (now.getTime() - lastAttemptTime.value.getTime()) > lockoutDuration) {
    attemptCount.value = 0;
  }

  if (attemptCount.value >= maxAttempts) {
    const timeRemaining = Math.ceil((lockoutDuration - (now.getTime() - (lastAttemptTime.value?.getTime() || 0))) / 60000);
    notification.value?.showNotification(
      `Trop de tentatives de connexion échouées. Réessayez dans ${timeRemaining} minutes.`,
      'error'
    );
    return false;
  }

  return true;
};

const handleSubmit = async (): Promise<void> => {
  // Vérifier la limitation de taux
  if (!checkRateLimit()) {
    return;
  }

  // Nettoyer les erreurs précédentes
  clearErrors();
  
  // Valider le formulaire
  if (!validateForm()) {
    notification.value?.showNotification(
      'Veuillez corriger les erreurs dans le formulaire.',
      'error'
    );
    return;
  }
  
  isSubmitting.value = true;
  
  const credentials: LoginRequest = {
    email: formData.email.trim().toLowerCase(),
    password: formData.password,
  };

  try {
    // Appel réel à l'API d'authentification
    await authService.login(credentials);
    // Émettre l'événement de soumission uniquement si succès
    emit('submit', credentials);
    // Réinitialiser le compteur de tentatives en cas de succès
    attemptCount.value = 0;
    lastAttemptTime.value = null;
  } catch (error) {
    const authError = error as AuthError;
    // Incrémenter le compteur de tentatives
    attemptCount.value++;
    lastAttemptTime.value = new Date();
    // Afficher l'erreur via notification
    const errorMessage = getErrorMessage(authError);
    notification.value?.showNotification(errorMessage, 'error');
    // Gestion spécifique selon le type d'erreur
    if (authError.code === 'INVALID_CREDENTIALS') {
      // Effacer le mot de passe pour forcer une nouvelle saisie
      formData.password = '';
      errors.password = 'Identifiants incorrects';
    } else if (authError.code === 'EMAIL_NOT_VERIFIED') {
      // Proposer de renvoyer l'email de validation
      // TODO: Implémenter la fonctionnalité de renvoi d'email
    }
    console.error('Erreur de connexion:', authError);
  } finally {
    isSubmitting.value = false;
  }
};

// Nettoyage au démontage du composant
const resetForm = (): void => {
  formData.email = '';
  formData.password = '';
  formData.rememberMe = false;
  clearErrors();
  attemptCount.value = 0;
  lastAttemptTime.value = null;
};

// Exposer les méthodes pour le composant parent
defineExpose({
  resetForm,
  clearErrors,
});
</script> 